import { type CombinedError, type Exchange, mapExchange } from "urql";

export const RELOADED_BUILD_STORAGE_KEY = "acto.schemaMismatchReloadedBuild";

export const DEPLOYED_BUILD_URL = `${import.meta.env.BASE_URL}version.json`;

// graphql-java classifies both a document the schema rejects and a variable it cannot coerce (how a removed input field
// arrives) as a `ValidationError` without a `code`. The api's own business-rule `ValidationError`s carry a `code`.
function isSchemaMismatch(error: CombinedError) {
  return error.graphQLErrors.some(
    ({ extensions }) => extensions?.["classification"] === "ValidationError" && extensions["code"] === undefined,
  );
}

async function fetchDeployedBuild(): Promise<string | null> {
  try {
    const response = await fetch(DEPLOYED_BUILD_URL, { cache: "no-store" });
    if (!response.ok) throw new Error(`${DEPLOYED_BUILD_URL} answered ${response.status}`);
    const { build } = (await response.json()) as { build?: unknown };
    return typeof build === "string" && build !== "" ? build : null;
  } catch (error) {
    console.warn("Could not read the deployed widget build, keeping the page", error);
    return null;
  }
}

function readReloadedBuild(): string | null {
  try {
    return sessionStorage.getItem(RELOADED_BUILD_STORAGE_KEY);
  } catch {
    return null;
  }
}

function recordReloadedBuild(buildVersion: string): boolean {
  try {
    sessionStorage.setItem(RELOADED_BUILD_STORAGE_KEY, buildVersion);
    return true;
  } catch {
    return false;
  }
}

/**
 * Reloads the page when the api rejects a query as invalid against its schema and a build other than the running one is
 * deployed. While the deployed build is still the running one, a reload would serve the same rejected bundle, so the
 * page stays and the next rejection checks again. It reloads at most once per deployed build per tab session, so a
 * stale cache serving the old bundle cannot loop. A rejected mutation never reloads, so its input stays on screen.
 * Without a build version, or without session storage to record the reload, it never reloads.
 */
export function schemaMismatchReloadExchange(buildVersion: string | undefined): Exchange {
  let inFlightDeployedBuildCheck: Promise<void> | null = null;

  async function reloadIfNewerBuildDeployed(runningBuild: string, error: CombinedError) {
    const deployedBuild = await fetchDeployedBuild();
    if (!deployedBuild || deployedBuild === runningBuild) return;
    if (readReloadedBuild() === deployedBuild) return;
    if (!recordReloadedBuild(deployedBuild)) return;

    console.warn(`The api rejected a query as invalid against its schema, reloading for build ${deployedBuild}`, error);
    window.location.reload();
  }

  return mapExchange({
    onError(error, operation) {
      if (!buildVersion || operation.kind === "mutation" || !isSchemaMismatch(error)) return;
      inFlightDeployedBuildCheck ??= reloadIfNewerBuildDeployed(buildVersion, error).finally(() => {
        inFlightDeployedBuildCheck = null;
      });
    },
  });
}
